import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { from, Observable, Subject } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { User } from 'app/core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { GAnalyticsService } from '../../../services/g-analytics.service';
import { AllDevicesLogoutDialogComponent } from './all-devices-logout-dialog/all-devices-logout-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { RestfulEndpoint } from '../../../services/restful-endpoint.service';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
  selector: 'user-menu',
  templateUrl: './user-menu.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'userMenu',
})
export class UserMenuComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar: boolean = true;
  user: User;
  isLoading = false;
  currentLang = localStorage.getItem('lang') || 'en';
  @ViewChild('signOutDialog') signOutDialog;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private afMessaging: AngularFireMessaging,
    private endPoint: RestfulEndpoint,
    private dialog: MatDialog,
    private googleServices: GAnalyticsService,
    private _toaster: ToastrService,
    private _transloco: TranslocoService,
    private userService: UserService,
    private _authService: AuthService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {
    this.user = await this.userService.getCurrentUser();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the user status
   *
   * @param status
   */
  updateUserStatus(status: string): void {
    // Return if user is not available
    if (!this.user) {
      return;
    }

    this.userService.updateUser({
      ...this.user,
      status,
    });
  }

  /**
   * Sign out
   */
  signOutModal(): void {
    const dialogRef = this.dialog.open(this.signOutDialog, {
      maxWidth: '640px',
    });
  }

  async signOut(type?: string): Promise<void> {
    if (type === 'all') {
      this.googleServices.setEvent('SignOut_AllDevices_Settings', 'Authentication');

      const dialogRef = this.dialog.open(AllDevicesLogoutDialogComponent, {
        width: '400px',
        disableClose: true, // Prevent closing on backdrop click
      });
    } else {
      this.googleServices.setEvent('SignOut_CurrentDevice_Settings', 'Authentication');
      await this._authService.signOut();
      this.dialog.closeAll();
    }
  }

  getDeviceId(): string {
    return localStorage.getItem('deviceId');
  }

  getFCmToken(): string {
    return localStorage.getItem('fcm_token');
  }

  deleteFCMToken(device_id: string): Observable<{
    status: 'success';
    message: 'Success';
    error_code: null;
    meta_data: null;
    data: [];
    errors: null;
  }> {
    return from(this.userService.getUserId()).pipe(
      switchMap((userId) => {
        return this.endPoint.delete(`v2/users/${userId}/delete-device-token/${device_id}`);
      }),
    );
  }
}
